<template>
    <!-- <div class="bg-background w-screen overflow-x-hidden flex flex-col flex-wrap grow">
      <main-header class="w-fixed w-full shrink grow-0" />
      <div class="max-w-7xl mx-auto grow ">
        <slot />
      </div>
      <default-footer />
    </div> -->

    <div class="flex flex-col min-h-screen bg-background text-text" :style="{ '--cms-top-offset': topOffset }">
        <div ref="topBar" class="fixed top-0 z-50 w-full">
            <MaintenanceBanner/>
            <main-header></main-header>
        </div>

        <main class="p-4 flex self-center grow mt-[var(--cms-top-offset,4rem)] pt-6 max-w-7xl mx-auto w-full bg-background h-auto">
            <slot/>
        </main>
        <Popup
            v-model="isOpen"
            :title="options.title"
            @ok="handleOk"
            @cancel="handleCancel"
            :type="options.type"
        >{{ options.body }}
        </Popup>

        <Popup
            v-model="progress_isOpen"
            :title="t('deletionProgress')"
            @ok="progress_handleOk"
            @cancel="progress_handleCancel"
            :disabled="!allDone"
            ok-only
        >
            <div class="flex flex-col gap-2">
                <div v-for="item in progress_options" :key="item.id" class="flex flex-row gap-x-2  items-center">
                    <div v-show="item.status === ProgressStatus.UNSTARTED">
                        <div class="w-4 h-4 fill-primary">
                            <MinusIcon/>
                        </div>
                    </div>
                    <div v-show="item.status === ProgressStatus.STARTED">
                        <div class="w-4 h-4 animate-spin  fill-primary">
                            <SpinnerIcon/>
                        </div>
                    </div>
                    <div v-show="item.status === ProgressStatus.SUCCESS">
                        <checkmark-icon class="fill-green-400 w-4 h-4"/>
                    </div>
                    <div v-show="item.status === ProgressStatus.ERROR">
                        <close-icon class="fill-red-400 w-4 h-4"/>
                    </div>
                    <div>{{ item.label }}</div>
                </div>
                <div
                    v-if="errorMessage"
                    class="border rounded-sm border-red-400 text-red-400 p-4"
                >{{ errorMessage }}
                </div>
                <section v-if="orphanedMedia.length" class="mt-4 border-t border-primary/30 pt-4">
                    <h3 class="font-bold text-lg">{{ t('orphanedMedia.title') }}</h3>
                    <p class="mb-4 text-sm">{{ t('orphanedMedia.description') }}</p>
                    <OrphanedMediaList :media="orphanedMedia" @delete="deleteOrphanedMedia"/>
                </section>
            </div>
        </Popup>

        <Popup
            v-model="orphanPrompt_isOpen"
            :title="t('unusedAfterSave.title')"
            close-on-backdrop
        >
            <div class="flex flex-col gap-4">
                <p class="text-sm">
                    {{ t('unusedAfterSave.description', { count: orphanPrompt_media.length }, orphanPrompt_media.length) }}
                </p>
                <OrphanedMediaList :media="orphanPrompt_media" @delete="delete_prompted_media"/>
                <div
                    v-if="orphanPrompt_error"
                    class="border rounded-sm border-red-400 text-red-400 p-4"
                >{{ orphanPrompt_error }}
                </div>
            </div>
            <template #footer>
                <y-button
                    v-if="orphanPrompt_remaining > 1"
                    type="destructive"
                    @click.prevent="delete_all_prompted_media"
                >
                    {{ t('unusedAfterSave.deleteAll', { count: orphanPrompt_remaining }) }}
                </y-button>
                <y-button type="basic" text-only @click.prevent="orphanPrompt_isOpen = false">
                    {{ orphanPrompt_remaining ? t('unusedAfterSave.keep') : t('unusedAfterSave.close') }}
                </y-button>
            </template>
        </Popup>

        <UploadTray/>

        <default-footer></default-footer>
    </div>

</template>

<script lang="ts" setup>
import { useElementSize } from '@vueuse/core'
import MainHeader from '../components/MainHeader.vue'
import DefaultFooter from '../components/footer/DefaultFooter.vue'

// The maintenance banner sits above the fixed header, so content (and sticky toolbars) offset by the measured height.
const topBar = ref<HTMLElement>()
const { height: topBarHeight } = useElementSize(topBar, undefined, { box: 'border-box' })
const { mode: maintenanceMode } = useMaintenance()
// Before it is measured (SSR), estimate a one-line banner rather than letting it cover the content.
const topOffset = computed(() => topBarHeight.value
    ? `${ topBarHeight.value }px`
    : maintenanceMode.value === 'soft' ? 'calc(4rem + 2.5rem)' : undefined)

const { isOpen, options, handleOk, handleCancel } = useConfirm()
const {
    isOpen: progress_isOpen,
    options: progress_options,
    handleOk: progress_handleOk,
    handleCancel: progress_handleCancel,
    ProgressStatus,
    allDone,
    errorMessage,
    orphanedMedia,
    deleteOrphanedMedia,
} = useProgressPopup()
const { t } = useI18n()

const {
    promptOpen: orphanPrompt_isOpen,
    promptMedia: orphanPrompt_media,
    promptError: orphanPrompt_error,
    delete_prompted_media,
    delete_all_prompted_media,
} = useOrphanedMedia()
const orphanPrompt_remaining = computed(() => orphanPrompt_media.value
    .filter(media => media.status === 'available' || media.status === 'error').length)

const themeClass = useThemeClass()

useHead(() => ({
    htmlAttrs: {
        class: themeClass.value
    }
}))

</script>

<style scoped lang="scss">
@use '../assets/scss/variables.scss';
@use '../assets/scss/theme.css';

html {
    width: 100vw;
}

// .wrapper {
//   // padding-left: calc(100vw - 100%);
// }
body {
    background-color: red;
}
</style>

<i18n lang="yaml">
en:
    deletionProgress: Deletion progress
    orphanedMedia:
        title: Unused media
        description: This media is no longer used. Delete each item separately, or close this window to keep it.
    unusedAfterSave:
        title: Assets no longer in use
        description: "After this save, the asset below is no longer used anywhere. Do you want to delete it permanently? Older versions of this entry that still refer to it will lose it. | After this save, the {count} assets below are no longer used anywhere. Do you want to delete them permanently? Older versions of this entry that still refer to them will lose them."
        deleteAll: Delete all ({count})
        keep: Keep assets
        close: Close
nl:
    deletionProgress: Voortgang verwijderen
    orphanedMedia:
        title: Ongebruikte media
        description: Deze media wordt niet meer gebruikt. Verwijder elk item afzonderlijk, of sluit dit venster om het te bewaren.
    unusedAfterSave:
        title: Assets niet meer in gebruik
        description: "Na deze wijziging wordt de onderstaande asset nergens meer gebruikt. Wil je deze definitief verwijderen? Oudere versies van dit item die er nog naar verwijzen raken deze kwijt. | Na deze wijziging worden de {count} onderstaande assets nergens meer gebruikt. Wil je ze definitief verwijderen? Oudere versies van dit item die er nog naar verwijzen raken ze kwijt."
        deleteAll: Alles verwijderen ({count})
        keep: Assets bewaren
        close: Sluiten
</i18n>
